import { PersistedWorkflow } from '@cotera/sdk/core';

const { WorkflowNodeType } = PersistedWorkflow;

/** Minimal node shape this check reads — just id and type. */
export type StructureIssueNode = {
  readonly id: string;
  readonly type: string;
};

/** Minimal edge shape this check reads. */
export type StructureIssueEdge = {
  readonly fromNodeId: string;
  readonly toNodeId: string;
};

/** Node id to the structural warnings on it. Absent means clean. */
export type WorkflowStructureIssuesByNodeId = ReadonlyMap<
  string,
  readonly string[]
>;

/**
 * The warning a group-by wired straight to a non-for-each step carries.
 *
 * A group-by does not fan out: it emits one `{ groups, groupCount, itemCount }`
 * object, and a for-each pointed at `groups` is what turns that into one run per
 * group. Wired directly to the next step, the whole list of groups is passed
 * through once — the next step runs a single time with every group at once, not
 * once per group. That reads as "it ran" while doing the wrong thing, so it is
 * worth a mark on the canvas rather than a surprise at run time.
 */
export const GROUP_BY_NEEDS_FOR_EACH_MESSAGE =
  'Add a For each (pointed at groups) after this to run once per group. ' +
  'Wired straight to the next step, the whole list of groups is passed ' +
  'through in one run.';

/**
 * Structural warnings for a whole graph, keyed by node id — the mistakes a node
 * can make by how it is *wired*, as opposed to a stale ref (see
 * `useWorkflowRefIssues`). Pure and cheap (one pass over the edges), so it can
 * run on every graph change without the sample-gathering the ref check needs.
 *
 * Today the one check is: a group-by whose output feeds anything other than a
 * for-each. A group-by with no successor yet is skipped — it is still being
 * wired, and warning about an unfinished branch is noise.
 */
export function workflowStructureIssues(graph: {
  readonly nodes: readonly StructureIssueNode[];
  readonly edges: readonly StructureIssueEdge[];
}): WorkflowStructureIssuesByNodeId {
  const nodeTypeById = new Map(
    graph.nodes.map((node) => [node.id, node.type] as const)
  );
  const byNodeId = new Map<string, readonly string[]>();

  for (const node of graph.nodes) {
    if (node.type !== WorkflowNodeType.GroupBy) {
      continue;
    }
    const successors = graph.edges.filter(
      (edge) => edge.fromNodeId === node.id
    );
    if (successors.length === 0) {
      continue;
    }
    const feedsNonForEach = successors.some(
      (edge) => nodeTypeById.get(edge.toNodeId) !== WorkflowNodeType.ForEach
    );
    if (feedsNonForEach) {
      byNodeId.set(node.id, [GROUP_BY_NEEDS_FOR_EACH_MESSAGE]);
    }
  }

  return byNodeId;
}
